/**
 * `qrDraftCounts.ts` — derive the Stats Strip counts (Diagnoses / Orders /
 * Medications) from a PRELIMINARY Progress Note's draft `QuestionnaireResponse`.
 *
 * Gate-clinical-writes-until-Lock: before Lock, no clinical FHIR resources
 * exist for the note — they're materialized only at Lock time. So the strip's
 * normal encounter-scoped `_summary=count` queries would all read 0 for a
 * preliminary note. Instead we count the PROPOSED items straight off the draft
 * QR, reusing the same hydrators the edit form uses so the numbers match what
 * the doctor sees in the note body.
 *
 *   - Diagnoses    → `assessment1` codings (encounter-diagnosis)
 *   - Orders       → `testsOrdered` codings (ServiceRequest)
 *   - Medications  → `medsReviewed` + `treatmentsPrescribed` codings
 *
 * After Lock (final), the strip reverts to counting the real chart resources.
 */
import type { QuestionnaireResponse } from '@medplum/fhirtypes'
import {
  hydrateConditionsFromQr,
  hydrateMedicationsFromQr,
  hydrateServiceRequestsFromQr,
} from '../ProgressNoteEdit/progressNoteQrHydrate'
import type { SoapNoteDto } from '../ProgressNoteEdit/draftToSoap'
import {
  hydrateConditionsFromScribeDraft,
  hydrateMedicationsFromScribeDraft,
  hydrateServiceRequestsFromScribeDraft,
} from '../ProgressNoteEdit/scribeDraftPayload'

export interface DraftCounts {
  diagnoses: number
  orders: number
  medications: number
}

export function qrDraftCounts(qr: QuestionnaireResponse | undefined): DraftCounts {
  const conditions = hydrateConditionsFromQr(qr)
  const diagnoses = conditions.filter((c) => c.category === 'encounter-diagnosis').length
  const orders = hydrateServiceRequestsFromQr(qr).length
  const medications = hydrateMedicationsFromQr(qr).length
  return { diagnoses, orders, medications }
}

/** Blob-first analogue of `qrDraftCounts` — same counts off the lossless blob, so the strip matches the body when the draft QR is unreadable. */
export function scribeDraftCounts(soapNote: SoapNoteDto): DraftCounts {
  const diagnoses = hydrateConditionsFromScribeDraft(soapNote).filter(
    (c) => c.category === 'encounter-diagnosis',
  ).length
  const orders = hydrateServiceRequestsFromScribeDraft(soapNote).length
  const medications = hydrateMedicationsFromScribeDraft(soapNote).length
  return { diagnoses, orders, medications }
}
